import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import Icons from 'assets/icons';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

const LABEL_SX: SxProps<Theme> = {
  fontSize: '1rem',
  lineHeight: '1.5rem',
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const ACTIVE_LABEL_SX: SxProps<Theme> = {
  fontWeight: 600,
  color: 'tokens.navy.800',
};

const INACTIVE_LABEL_SX: SxProps<Theme> = {
  fontWeight: 400,
  color: 'tokens.navy.500',
};

type StepIconProps = {
  isActive: boolean;
  isCompleted: boolean;
  readOnly: boolean;
};

const StepIcon = ({ isActive, isCompleted, readOnly }: StepIconProps) => {
  if (isActive && readOnly) {
    return <Icons.CheckOld customClass="size-4" />;
  }

  if (isActive) {
    return <Icons.EditPencil customClass="w-4 h-4" />;
  }

  if (isCompleted) {
    return <Icons.CheckOld customClass="w-4 h-4 text-navy-500" />;
  }

  return <Icons.CircleDotted customClass="w-4 h-4 text-navy-500" />;
};

type Step = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

type AidStudioStepperProps = {
  steps: Step[];
  activeStep: string;
  onStepChange?: (value: string) => void;
  completedSteps?: string[];
  readOnly?: boolean;
};

const AidStudioStepper = ({
  steps,
  activeStep,
  onStepChange,
  completedSteps = [],
  readOnly = false,
}: AidStudioStepperProps) => {
  const activeIndex = steps.findIndex((s: Step) => s.value === activeStep);

  const handleStepClick = (step: Step) => {
    if (!step.isDisabled && onStepChange) {
      onStepChange(step.value);
    }
  };

  return (
    <nav
      className="flex gap-2 items-center py-1 rounded-md"
      data-testid="stepper"
      aria-label="Progress"
    >
      {steps.map((step: Step, index: number) => {
        const isActive = activeStep === step.value;
        const isCompleted =
          completedSteps.includes(step.value) || index < activeIndex;
        const isDisabled = step.isDisabled && !isCompleted;
        const isClickable = !isDisabled && onStepChange;
        const isLast = index === steps.length - 1;
        const stepStateLabel = isActive
          ? 'current step'
          : isCompleted
          ? 'completed'
          : 'not started';
        const stepAriaLabel = `${step.label}, ${stepStateLabel}`;

        const stepContent = (
          <>
            <StepIcon
              isActive={isActive}
              isCompleted={isCompleted}
              readOnly={readOnly}
            />
            <Box
              component="span"
              sx={[
                LABEL_SX,
                isActive && ACTIVE_LABEL_SX,
                !isActive && INACTIVE_LABEL_SX,
              ]}
            >
              {step.label}
            </Box>
          </>
        );

        return (
          <React.Fragment key={step.value}>
            {isClickable ? (
              <button
                type="button"
                onClick={() => handleStepClick(step)}
                className="flex gap-1 items-center bg-transparent border-none p-0 cursor-pointer hover:opacity-80"
                data-testid={`step-${step.value}`}
                aria-current={isActive ? 'step' : undefined}
                aria-label={stepAriaLabel}
              >
                {stepContent}
              </button>
            ) : (
              <Stack
                direction="row"
                data-testid={`step-${step.value}`}
                aria-current={isActive ? 'step' : undefined}
                aria-disabled={isDisabled ? true : undefined}
                aria-label={stepAriaLabel}
                sx={{ gap: 0.5, alignItems: 'center' }}
              >
                {stepContent}
              </Stack>
            )}
            {!isLast && (
              <Icons.ChevronRight customClass="w-3.5 h-3.5 text-navy-200 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default AidStudioStepper;
